import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from './navigation';
import { BadgeCheck, BellRing, CheckCheck, ChevronDown, ChevronUp, Command, Folder, Grid2X2, HelpCircle, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Settings, Sparkles, Trash2, UserPlus, UserRound, Zap } from 'lucide-react';
import { get } from './api';
import { useApp } from './AppContext';
import { Avatar, Bell, BrandIcon, BrandLogo, Button, IconButton, Modal, Search, X, iconMap, useModalScrollLock } from './components';
import { emitHaptic } from './haptics';
import { isMobileAppRuntime } from './mobile-runtime';
import { nosivoGreeting } from './time';
import type { Paged, Task } from './types';
import { UndoHost } from './UndoHost';

interface SearchResult {
  id: number;
  title: string;
  type: string;
  context?: string;
  status?: string;
  path: string;
}

type MobileNavDragState = {
  active: boolean;
  dragging: boolean;
  settling: boolean;
  position: number;
  targetIndex: number;
  stretch: number;
  direction: number;
};

export function AppShell() {
  const { bootstrap, logout, markNotificationRead, markAllNotificationsRead, clearNotifications, acknowledgeNotification } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationAction, setNotificationAction] = useState<'reading' | 'clearing' | null>(null);
  const [notificationError, setNotificationError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [taskCounts, setTaskCounts] = useState({ backlog: 0, doing: 0, review: 0, done: 0 });
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);
  const mobileNavPointerRef = useRef({ pointerId: -1, startX: 0, lastX: 0, dragging: false, targetIndex: 0 });
  const mobileNavSettleTimerRef = useRef<number | null>(null);
  const suppressMobileNavClickRef = useRef(false);
  const [mobileNavDrag, setMobileNavDrag] = useState<MobileNavDragState>({ active: false, dragging: false, settling: false, position: 0, targetIndex: 0, stretch: 0, direction: 0 });
  const appMode = isMobileAppRuntime();
  const [compactViewport, setCompactViewport] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
  const [mobileGreeting, setMobileGreeting] = useState(() => nosivoGreeting());
  const mobilePresentation = appMode || compactViewport;
  useModalScrollLock(searchOpen || (mobilePresentation && (mobileOpen || notificationOpen || profileOpen)));

  useEffect(() => {
    setMobileOpen(false);
    setNotificationOpen(false);
    setProfileOpen(false);
    setMobileNavDrag((value) => ({ ...value, active: false, dragging: false, stretch: 0, direction: 0 }));
  }, [location.pathname]);
  useEffect(() => () => {
    if (mobileNavSettleTimerRef.current !== null) window.clearTimeout(mobileNavSettleTimerRef.current);
  }, []);
  useEffect(() => {
    const viewport = window.matchMedia('(max-width: 900px)');
    const syncViewport = () => setCompactViewport(viewport.matches);
    syncViewport();
    viewport.addEventListener('change', syncViewport);
    return () => viewport.removeEventListener('change', syncViewport);
  }, []);
  useEffect(() => {
    const refreshGreeting = () => setMobileGreeting(nosivoGreeting());
    refreshGreeting();
    const interval = window.setInterval(refreshGreeting, 60_000);
    return () => window.clearInterval(interval);
  }, []);
  useEffect(() => {
    document.documentElement.classList.toggle('nosivo-compact-layout', compactViewport && !appMode);
    return () => document.documentElement.classList.remove('nosivo-compact-layout');
  }, [appMode, compactViewport]);
  useEffect(() => {
    if (!mobilePresentation || !mobileOpen) return;
    document.body.classList.add('mobile-drawer-open');
    return () => document.body.classList.remove('mobile-drawer-open');
  }, [mobileOpen, mobilePresentation]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  useEffect(() => {
    if (!profileOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) setProfileOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProfileOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [profileOpen]);
  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    const timeout = window.setTimeout(async () => {
      const data = await get<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(search)}`);
      setResults(data.results);
    }, 220);
    return () => window.clearTimeout(timeout);
  }, [search]);
  useEffect(() => {
    if (!bootstrap) return;
    let active = true;
    const loadTaskCounts = async () => {
      try {
        const data = await get<Paged<Task>>('/api/tasks?limit=100');
        if (!active) return;
        setTaskCounts({
          backlog: data.items.filter((task) => task.status === 'todo').length,
          doing: data.items.filter((task) => task.status === 'doing').length,
          review: data.items.filter((task) => task.status === 'review').length,
          done: data.items.filter((task) => task.status === 'done').length,
        });
      } catch {
        // Navigation remains usable if task totals are temporarily unavailable.
      }
    };
    const refreshTaskCounts = () => void loadTaskCounts();
    void loadTaskCounts();
    window.addEventListener('nosivo:tasks-updated', refreshTaskCounts);
    return () => { active = false; window.removeEventListener('nosivo:tasks-updated', refreshTaskCounts); };
  }, [bootstrap?.user.id]);

  const pageTitle = useMemo(() => {
    if (location.pathname === '/') return 'Home';
    const active = bootstrap?.navigation
      .filter((item) => item.path !== '/' && location.pathname.startsWith(item.path))
      .sort((left, right) => right.path.length - left.path.length)[0];
    return active?.label || location.pathname.split('/')[1]?.replace(/-/g, ' ') || 'Nosivo';
  }, [bootstrap?.navigation, location.pathname]);

  if (!bootstrap) return null;
  const { user, navigation, notifications, unreadNotifications } = bootstrap;
  const firstName = user.name.split(' ')[0] || 'there';
  const requiredAnnouncement = notifications.find((item) => !item.is_read && item.requires_acknowledgement);
  const findNavigation = (path: string) => navigation.find((item) => item.path === path);
  const dashboardNavigation = findNavigation('/');
  const calendarNavigation = findNavigation('/schedule');
  const taskNavigation = findNavigation('/tasks');
  const footerNavigation = [findNavigation('/support'), findNavigation('/settings'), findNavigation('/people')].filter(Boolean);
  const reservedPaths = new Set(['/', '/tasks', '/schedule', '/support', '/settings', '/people']);
  const navigationSections = navigation.filter((item) => !reservedPaths.has(item.path)).reduce<Array<{ label: string; items: typeof navigation }>>((groups, item) => {
    const label = item.section || 'Workspace';
    const existing = groups.find((group) => group.label === label);
    if (existing) existing.items.push(item);
    else groups.push({ label, items: [item] });
    return groups;
  }, []);
  const activeTaskStatus = location.pathname === '/tasks' ? new URLSearchParams(location.search).get('status') : null;
  const taskGroups = [
    { label: 'Backlog', status: 'todo', count: taskCounts.backlog, icon: Folder, className: 'backlog' },
    { label: 'In progress', status: 'doing', count: taskCounts.doing, icon: Zap, className: 'doing' },
    { label: 'Validation', status: 'review', count: taskCounts.review, icon: Sparkles, className: 'review' },
    { label: 'Done', status: 'done', count: taskCounts.done, icon: BadgeCheck, className: 'done' },
  ];
  const mobileNavigation = user.role === 'employee' ? [
    navigation.find((item) => item.path === '/') || { label: 'Home', path: '/', icon: 'home' },
    navigation.find((item) => item.path === '/tasks') || { label: 'Tasks', path: '/tasks', icon: 'tasks' },
    navigation.find((item) => item.path === '/attendance') || { label: 'Attendance', path: '/attendance', icon: 'attendance' },
    { ...(navigation.find((item) => item.path === '/email') || { path: '/email', section: 'Tools' }), label: 'Email', icon: 'email' },
    { label: 'More', path: '/more', icon: 'more' },
  ] : user.role === 'client' ? [
    navigation.find((item) => item.path === '/') || { label: 'Home', path: '/', icon: 'home' },
    navigation.find((item) => item.path === '/tasks') || { label: 'Tasks', path: '/tasks', icon: 'tasks' },
    navigation.find((item) => item.path === '/approvals') || { label: 'Approvals', path: '/approvals', icon: 'approvals' },
    navigation.find((item) => item.path === '/notifications') || { label: 'Inbox', path: '/notifications', icon: 'notifications' },
    { label: 'More', path: '/more', icon: 'more' },
  ] : [
    navigation.find((item) => item.path === '/') || { label: 'Home', path: '/', icon: 'home' },
    navigation.find((item) => item.path === '/tasks') || { label: 'Tasks', path: '/tasks', icon: 'tasks' },
    navigation.find((item) => item.path === '/people') || { label: 'People', path: '/people', icon: 'people' },
    navigation.find((item) => item.path === '/notifications') || { label: 'Inbox', path: '/notifications', icon: 'notifications' },
    { label: 'More', path: '/more', icon: 'more' },
  ];
  const mobilePrimaryPaths = new Set(mobileNavigation.filter((item) => item.path !== '/more').map((item) => item.path));
  const mobileMorePaths = navigation.filter((item) => !mobilePrimaryPaths.has(item.path)).map((item) => item.path);
  const matchesMobilePath = (path: string) => location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
  const matchingPrimaryIndex = mobileNavigation.findIndex((item) => item.path !== '/more' && matchesMobilePath(item.path));
  const activeMobileIndex = matchingPrimaryIndex >= 0 ? matchingPrimaryIndex : mobileNavigation.length - 1;
  const mobileNavTargetIndex = mobileNavDrag.active ? mobileNavDrag.targetIndex : activeMobileIndex;

  const mobileNavPoint = (clientX: number) => {
    const nav = mobileNavRef.current;
    if (!nav) return { position: activeMobileIndex, targetIndex: activeMobileIndex };
    const rect = nav.getBoundingClientRect();
    const inset = 5;
    const itemWidth = Math.max((rect.width - inset * 2) / mobileNavigation.length, 1);
    const position = Math.max(0, Math.min(mobileNavigation.length - 1, (clientX - rect.left - inset - itemWidth / 2) / itemWidth));
    return { position, targetIndex: Math.round(position) };
  };

  const startMobileNavGesture = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    const point = mobileNavPoint(event.clientX);
    mobileNavPointerRef.current = { pointerId: event.pointerId, startX: event.clientX, lastX: event.clientX, dragging: false, targetIndex: point.targetIndex };
    event.currentTarget.setPointerCapture(event.pointerId);
    setMobileNavDrag({ active: true, dragging: false, settling: false, position: point.position, targetIndex: point.targetIndex, stretch: .035, direction: 0 });
  };

  const moveMobileNavGesture = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = mobileNavPointerRef.current;
    if (gesture.pointerId !== event.pointerId) return;
    const point = mobileNavPoint(event.clientX);
    const delta = event.clientX - gesture.lastX;
    const dragging = gesture.dragging || Math.abs(event.clientX - gesture.startX) > 4;
    const direction = Math.abs(delta) > .4 ? Math.sign(delta) : 0;
    if (dragging) event.preventDefault();
    if (dragging && point.targetIndex !== gesture.targetIndex) emitHaptic('selection', { source: 'mobile-nav-drag' });
    mobileNavPointerRef.current = { ...gesture, lastX: event.clientX, dragging, targetIndex: point.targetIndex };
    setMobileNavDrag({ active: true, dragging, settling: false, position: point.position, targetIndex: point.targetIndex, stretch: Math.min(.16, .035 + Math.abs(delta) / 45), direction });
  };

  const finishMobileNavGesture = (event: ReactPointerEvent<HTMLElement>, cancelled = false) => {
    const gesture = mobileNavPointerRef.current;
    if (gesture.pointerId !== event.pointerId) return;
    const point = mobileNavPoint(event.clientX);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    mobileNavPointerRef.current = { pointerId: -1, startX: 0, lastX: 0, dragging: false, targetIndex: point.targetIndex };
    if (!cancelled) {
      suppressMobileNavClickRef.current = true;
      navigate(mobileNavigation[point.targetIndex].path);
      window.setTimeout(() => { suppressMobileNavClickRef.current = false; }, 320);
    }
    setMobileNavDrag({ active: false, dragging: false, settling: !cancelled, position: point.targetIndex, targetIndex: point.targetIndex, stretch: 0, direction: 0 });
    if (mobileNavSettleTimerRef.current !== null) window.clearTimeout(mobileNavSettleTimerRef.current);
    mobileNavSettleTimerRef.current = window.setTimeout(() => setMobileNavDrag((value) => ({ ...value, settling: false })), 520);
  };

  const markAllRead = async () => {
    if (!unreadNotifications || notificationAction) return;
    setNotificationAction('reading');
    setNotificationError('');
    try { await markAllNotificationsRead(); }
    catch { setNotificationError('Could not update notifications. Please try again.'); }
    finally { setNotificationAction(null); }
  };

  const clearAllNotifications = async () => {
    if (!notifications.length || notificationAction) return;
    setNotificationAction('clearing');
    setNotificationError('');
    const finalDelay = Math.min(Math.max(notifications.length - 1, 0), 10) * 28;
    await new Promise<void>((resolve) => window.setTimeout(resolve, 280 + finalDelay));
    try { await clearNotifications(); }
    catch {
      setNotificationError('Could not clear notifications. Please try again.');
      setNotificationAction(null);
      return;
    }
    setNotificationAction(null);
  };

  return <div className={`app-shell ${user.role === 'client' ? 'app-shell--client' : 'app-shell--company'} app-shell--role-${user.role} ${mobilePresentation ? 'app-shell--mobile-app' : ''} ${compactViewport && !appMode ? 'app-shell--compact-preview' : ''} ${collapsed ? 'app-shell--collapsed' : ''} ${mobileOpen ? 'app-shell--mobile-open' : ''}`}>
    <aside className="sidebar">
      <div className="sidebar__mobile-profile">
        <div className="sidebar__mobile-profile-bar"><BrandIcon /><IconButton label="Close navigation" onClick={() => setMobileOpen(false)}><X size={18} /></IconButton></div>
        <Avatar name={user.name} color={user.avatar_color} imageUrl={user.avatar_url} size="lg" />
        <div className="sidebar__mobile-profile-copy"><strong>{user.name}</strong><span>{user.job_title || user.role}</span><small>{user.email}</small></div>
        <div className="sidebar__mobile-profile-actions">
          {user.role !== 'client' && <Link to="/profile">View profile</Link>}
          <Link to="/notifications"><BellRing size={14} />Alerts{unreadNotifications > 0 && <em>{Math.min(unreadNotifications, 99)}</em>}</Link>
        </div>
      </div>
      <div className="sidebar__brand"><BrandLogo surface="dark" /><button type="button" className="sidebar__brand-icon-button" aria-label="Expand navigation" onClick={() => setCollapsed(false)}><BrandIcon /></button><IconButton label={collapsed ? 'Expand navigation' : 'Collapse navigation'} onClick={() => setCollapsed((value) => !value)}>{collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}</IconButton></div>
      <nav className="sidebar__primary" aria-label="Primary navigation">
        <section className="sidebar-section">
          <div className="sidebar-section__title"><span>Overview</span><ChevronDown /></div>
          {dashboardNavigation && <NavLink className="sidebar-link" to={dashboardNavigation.path} end title={collapsed ? 'Dashboard' : undefined}>{(() => { const Icon = iconMap[dashboardNavigation.icon] || Grid2X2; return <Icon />; })()}<span className="sidebar-link__label">Dashboard</span></NavLink>}
          {calendarNavigation && <NavLink className="sidebar-link" to={calendarNavigation.path} title={collapsed ? 'Calendar' : undefined}>{(() => { const Icon = iconMap[calendarNavigation.icon] || Command; return <Icon />; })()}<span className="sidebar-link__label">Calendar</span></NavLink>}
          {taskNavigation && <>
            <div className="sidebar-task-row">
              <NavLink className={`sidebar-task-parent ${location.pathname.startsWith('/tasks') ? 'is-active' : ''}`} to={taskNavigation.path} title={collapsed ? 'Tasks' : undefined}>{(() => { const Icon = iconMap[taskNavigation.icon] || Folder; return <Icon />; })()}<span className="sidebar-link__label">Tasks</span></NavLink>
              <button type="button" className="sidebar-task-toggle" aria-label={tasksExpanded ? 'Collapse task groups' : 'Expand task groups'} aria-expanded={tasksExpanded} onClick={() => setTasksExpanded((value) => !value)}>{tasksExpanded ? <ChevronUp /> : <ChevronDown />}</button>
            </div>
            {tasksExpanded && <div className="sidebar-task-tree">{taskGroups.map((group) => { const Icon = group.icon; return <Link className={`sidebar-link sidebar-link--${group.className} ${activeTaskStatus === group.status ? 'active' : ''}`} to={`/tasks?status=${group.status}`} key={group.status} aria-current={activeTaskStatus === group.status ? 'page' : undefined} title={collapsed ? group.label : undefined}><Icon /><span className="sidebar-link__label">{group.label}</span><span className="sidebar-link__count">{group.count}</span></Link>; })}</div>}
          </>}
        </section>
        {navigationSections.map((section) => <section className="sidebar-section" key={section.label}>
          <div className="sidebar-section__title"><span>{section.label === 'Connect' ? 'Tools' : section.label}</span><ChevronDown /></div>
          {section.items.map((item) => { const Icon = iconMap[item.icon] || Command; return <NavLink className="sidebar-link" key={item.path} to={item.path} title={collapsed ? item.label : undefined}><Icon /><span className="sidebar-link__label">{item.label}</span>{item.path === '/notifications' && unreadNotifications > 0 && <span className="sidebar-link__badge">{Math.min(unreadNotifications, 99)}</span>}</NavLink>; })}
        </section>)}
        <section className="sidebar-section sidebar__utilities" aria-label="Workspace utilities">
        <div className="sidebar-section__title"><span>Workspace</span></div>
        <div className="sidebar__footer-links">{footerNavigation.map((item) => {
          if (!item) return null;
          const presentation = item.path === '/support' ? { label: 'Help Center', icon: HelpCircle } : item.path === '/people' ? { label: 'Invite Team', icon: UserPlus } : { label: 'Settings', icon: Settings };
          const Icon = presentation.icon;
          return <NavLink className="sidebar-link" key={item.path} to={item.path} title={collapsed ? presentation.label : undefined}><Icon /><span className="sidebar-link__label">{presentation.label}</span></NavLink>;
        })}</div>
        </section>
      </nav>
      <div className="sidebar__footer">
        {bootstrap.workspace && <div className="sidebar__workspace"><span className="workspace-mark">{bootstrap.workspace.name.slice(0, 2).toUpperCase()}</span><div><strong>{bootstrap.workspace.name}</strong><small>{bootstrap.workspace.slug} · {bootstrap.workspace.plan}</small></div></div>}
        <div className="sidebar__user"><Avatar name={user.name} color={user.avatar_color} imageUrl={user.avatar_url} /><div><strong>{user.name}</strong><small>{user.email}</small></div><IconButton label="Sign out" onClick={() => void logout()}><LogOut size={17} /></IconButton></div>
      </div>
    </aside>
    {mobileOpen && <button className="sidebar-backdrop" aria-label="Close menu" onClick={() => setMobileOpen(false)} />}
    <div className="app-main">
      <header className="topbar">
        <div className="topbar__left"><IconButton label="Open navigation" onClick={() => setMobileOpen(true)}><Menu size={20} /></IconButton><button type="button" className="mobile-profile-trigger" aria-label="Open account and navigation" aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)}><Avatar name={user.name} color={user.avatar_color} imageUrl={user.avatar_url} size="sm" /><i /></button><div><span className="topbar__page-title">{pageTitle}</span><span className="topbar__greeting">{mobileGreeting}</span><small className="topbar__workspace-name">{bootstrap.workspace?.name || 'Nosivo'}</small><small className="topbar__mobile-context">{firstName} · {bootstrap.workspace?.name || 'Nosivo'}</small></div></div>
        <button type="button" className="command-search" aria-label="Search tasks, projects and people" onClick={() => setSearchOpen(true)}><Search size={16} /><span className="command-search__desktop-label">Search tasks, projects, people…</span><span className="command-search__mobile-label">Search</span><kbd>⌘ K</kbd></button>
        <div className="topbar__right">
          <div className="notification-wrap"><IconButton label="Notifications" aria-haspopup="dialog" aria-expanded={notificationOpen} onClick={() => { setNotificationOpen((value) => !value); setProfileOpen(false); setNotificationError(''); }}><Bell size={19} />{unreadNotifications > 0 && <span className="notification-dot">{unreadNotifications}</span>}</IconButton>
            {notificationOpen && <div className="notification-panel" role="dialog" aria-label="Notifications">
              <header>
                <div className="notification-panel__title"><strong>Notifications</strong><small>{unreadNotifications ? `${unreadNotifications} unread` : 'You are all caught up'}</small></div>
                <div className="notification-panel__header-actions">
                  <button type="button" onClick={() => void markAllRead()} disabled={!unreadNotifications || Boolean(notificationAction)}><CheckCheck size={13} />{notificationAction === 'reading' ? 'Marking…' : 'Mark all as read'}</button>
                  <button type="button" className="notification-panel__clear" onClick={() => void clearAllNotifications()} disabled={!notifications.some((item) => !item.sticky_key) || Boolean(notificationAction)}><Trash2 size={12} />{notificationAction === 'clearing' ? 'Clearing…' : 'Clear'}</button>
                  <IconButton label="Close notifications" onClick={() => setNotificationOpen(false)}><X size={16} /></IconButton>
                </div>
              </header>
              {notificationError && <div className="notification-panel__error" role="status">{notificationError}</div>}
              <div className={`notification-list ${notificationAction === 'clearing' ? 'is-clearing' : ''}`}>{notifications.map((item, index) => <button key={item.id} className={`${item.is_read ? '' : 'unread'} ${item.sticky_key ? 'is-sticky' : ''}`.trim()} disabled={Boolean(notificationAction)} style={{ '--notification-clear-delay': `${Math.min(index, 10) * 28}ms` } as CSSProperties} onClick={async () => { if (!item.is_read) await markNotificationRead(item.id); if (item.action_url) navigate(item.action_url); }}><span className={`notification-icon notification-icon--${item.type}`}><Bell size={14} /></span><span><strong>{item.title}{item.sticky_key && <em>Action required</em>}</strong><small>{item.message}</small></span></button>)}{!notifications.length && <div className="notification-list__empty"><BellRing size={18} /><strong>No notifications</strong><small>New updates will appear here.</small></div>}</div>
              <NavLink to="/notifications">View all notifications</NavLink>
            </div>}
          </div>
          <div className="profile-menu-wrap" ref={profileMenuRef}>
            <button type="button" className="topbar__identity" aria-label="Open account menu" aria-haspopup="menu" aria-expanded={profileOpen} onClick={() => { setProfileOpen((value) => !value); setNotificationOpen(false); }}><Avatar name={user.name} color={user.avatar_color} imageUrl={user.avatar_url} size="sm" /><div><strong>{user.name.split(' ')[0]}</strong><small>{user.role}</small></div><ChevronDown size={14} /></button>
            {profileOpen && <div className="account-menu" role="menu">
              <div className="account-menu__header"><Avatar name={user.name} color={user.avatar_color} imageUrl={user.avatar_url} /><span><strong>{user.name}</strong><small>{user.email}</small></span></div>
              <nav aria-label="Account shortcuts">
                {user.employee_id && <Link to="/profile" role="menuitem"><UserRound size={15} />Manage profile</Link>}
                <Link to="/notifications" role="menuitem"><BellRing size={15} />Notifications{unreadNotifications > 0 && <em>{Math.min(unreadNotifications, 99)}</em>}</Link>
                {bootstrap.capabilities.admin && <Link to="/settings" role="menuitem"><Settings size={15} />Settings</Link>}
              </nav>
              <button type="button" className="account-menu__logout" role="menuitem" onClick={() => { setProfileOpen(false); void logout(); }}><LogOut size={15} />Sign out</button>
            </div>}
          </div>
        </div>
      </header>
      <main className="page"><Outlet /></main>
      <nav
        ref={mobileNavRef}
        className={`mobile-nav ${mobileNavDrag.active ? 'is-pressing' : ''} ${mobileNavDrag.dragging ? 'is-dragging' : ''} ${mobileNavDrag.settling ? 'is-settling' : ''}`}
        aria-label="Mobile navigation"
        style={{
          '--mobile-nav-index': activeMobileIndex,
          '--mobile-nav-position': `${(mobileNavDrag.active ? mobileNavDrag.position : activeMobileIndex) * 100}%`,
          '--mobile-nav-stretch': 1 + mobileNavDrag.stretch,
          '--mobile-nav-lean': `${mobileNavDrag.direction * -1.8}deg`,
          '--mobile-nav-tail': `${mobileNavDrag.direction * -7}px`,
          '--mobile-nav-highlight-shift': `${mobileNavDrag.direction * 2}px`,
        } as CSSProperties}
        onPointerDown={startMobileNavGesture}
        onPointerMove={moveMobileNavGesture}
        onPointerUp={(event) => finishMobileNavGesture(event)}
        onPointerCancel={(event) => finishMobileNavGesture(event, true)}
      >
        <span className="mobile-nav__indicator" aria-hidden="true" />
        {mobileNavigation.map((item, index) => {
          const Icon = iconMap[item.icon] || Grid2X2;
          const active = index === activeMobileIndex;
          const liquidTarget = index === mobileNavTargetIndex;
          return <NavLink
            className={`${active ? 'active' : ''} ${liquidTarget ? 'is-liquid-target' : ''}`.trim()}
            aria-current={active ? 'page' : undefined}
            data-haptic="none"
            draggable={false}
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            activePaths={item.path === '/more' ? mobileMorePaths : []}
            onClick={(event) => {
              if (!suppressMobileNavClickRef.current) return;
              event.preventDefault();
              suppressMobileNavClickRef.current = false;
            }}
            onDragStart={(event) => event.preventDefault()}
          ><span className="mobile-nav__icon"><Icon size={19} />{item.path === '/notifications' && unreadNotifications > 0 && <em>{Math.min(unreadNotifications, 99)}</em>}</span><span>{item.label}</span></NavLink>;
        })}
      </nav>
    </div>
    {searchOpen && <div className="command-modal" onMouseDown={(event) => { if (event.target === event.currentTarget) setSearchOpen(false); }}><div className="command-dialog"><div className="command-input"><Search size={20} /><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search anything in Nosivo" /><IconButton label="Close search" onClick={() => setSearchOpen(false)}><X size={17} /></IconButton></div><div className="command-results">{search.trim().length < 2 ? <div className="command-hint"><Command size={20} /><div><strong>Find work instantly</strong><small>Search tasks, projects and people by name.</small></div></div> : results.length ? results.map((result) => <button key={`${result.type}-${result.id}`} onClick={() => { navigate(result.path); setSearchOpen(false); setSearch(''); }}><span className={`result-type result-type--${result.type}`}>{result.type[0].toUpperCase()}</span><span><strong>{result.title}</strong><small>{result.context || result.status}</small></span><em>{result.type}</em></button>) : <div className="command-hint"><Search size={20} /><div><strong>No matching results</strong><small>Try a project code, person or task keyword.</small></div></div>}</div></div></div>}
    <Modal open={Boolean(requiredAnnouncement)} dismissible={false} onClose={() => undefined} title={requiredAnnouncement?.title || 'Company announcement'} description="Please read this announcement before continuing." footer={<Button onClick={() => requiredAnnouncement && void acknowledgeNotification(requiredAnnouncement.id)}>I have read and acknowledge</Button>}><div className="mandatory-announcement"><Bell size={22} /><p>{requiredAnnouncement?.message}</p>{requiredAnnouncement?.action_url && <Button variant="secondary" onClick={() => navigate(requiredAnnouncement.action_url || '/')}>Open related page</Button>}</div></Modal>
    <UndoHost />
  </div>;
}
